<script>
  // This component holds the map
  import Details from '../Modal/Details.svelte';
  import { COUNTRY, COUNTRIES, CURRENT_COUNTRY } from '../stores.js';
  import { colors, PATH_MAP, VALUES, MAP_KEY, MAP_COUNTRY_CODE } from '../config.js';
  import { gs } from '../scripts/utils.js';
  import tooltip from '../scripts/tooltip.js';
  import { watchResize } from 'svelte-watch-resize';
  import { onMount, getContext } from 'svelte';
  import { geoPath, geoAzimuthalEqualArea } from 'd3-geo'
  import { feature } from 'topojson'
  import find from 'lodash/find'
  import get from 'lodash/get'
  import isArray from 'lodash/isArray'
  import map from 'lodash/map'
  import isUndefined from 'lodash/isUndefined'

  const { open } = getContext('simple-modal');

  // List of currently hovered countries
  let activeCountries;
  COUNTRY.subscribe(value => { activeCountries = value; });

  // Function to change currently hovered countries
  function changeCountries (value) {
    COUNTRY.update(v => isArray(value) ? value : [value]);
  }

  // Country data
  let data;
  COUNTRIES.subscribe(value => { data = value; });

  let world = {}; // Stores the world map. Is loaded onMount

  // Default sizes. Will be changed later
  let width = 100;
  let height = 50;

  $: countries = get(world, ['objects', MAP_KEY], {})

  // Extract the countries from the geojson
  $: mapFeatures = feature(world, countries)

  // Aspect ratio is 2:1
  $: size = Math.min(width, height * 2)

  // Set the viewbox based on the size
  $: viewBox = `0 0 ${size} ${size / 2}`

  // Select the projection and fit into the current dimensions
  // Centroid from https://dropchop.io/
  // Projection: https://github.com/d3/d3-geo-projection#geoAzimuthalEqualArea
  // https://en.m.wikipedia.org/wiki/Lambert_azimuthal_equal-area_projection
  $: projection = geoAzimuthalEqualArea().rotate([-13.132300182580336, -51.6418751333634]).precision(.1).fitSize([width, height], mapFeatures)

  // Build map projection function
  $: mapScale = geoPath().projection(projection)

  // Loop over each country
  $: elements = map(get(mapFeatures, 'features', []), (country, i) => {
    const d = mapScale(country)
    // Get the country code from the map feature
    const country_code = get(country, ['properties', MAP_COUNTRY_CODE])

    // Search for country data that has the same country code
    const datum = find(data, { 'code': country_code })

    if (isUndefined(datum)) {
      return {
        i,
        disabled: true,
        d,
        color: colors.notAvailable
      }
    }

    // First check for name in country data. If not available check for name in map data.
    const label = get(datum, 'label', get(country, ['properties', 'name'], false))
    const status = get(datum, 'status')
    const color = get(VALUES, status, colors.other)
    const id = get(datum, 'id')

    const content = `<div class="info"><strong>${label}</strong><strong class="dd">${gs(datum, 'status')}</strong><span>${'Klicken für weitere Informationen'}</span></div>`

    return {
      id,
      i,
      disabled: false,
      tooltip: { content },
      d,
      color
    }
  })

  // Get the list of active countries
  $: activeElements = map(activeCountries, country => {
    return get(elements, country)
  })

  // Fetch the map data on mount
  onMount(async () => {
    const res = await fetch(PATH_MAP);
    world = await res.json();
  });

  function resizeMap (node) {
    // Extract the with and height of the container
    ({ width, height } = node.getBoundingClientRect());
  }

  const showSurprise = (id) => {
    CURRENT_COUNTRY.update(() => id);
    open(Details);
  };
</script>

<div class="page-map" role="main">
  <svg class="vis" {viewBox} preserveAspectRatio="xMinYMin meet" use:watchResize={resizeMap}>
    <g>
      <rect
        x="0"
        y="0"
        {height}
        {width} />
      <g>
        {#each elements as country}
        <path
          class:disabled={country.disabled}
          d={country.d}
          fill={country.color}
          use:tooltip={country.tooltip}
          on:click={() => showSurprise(country.id)}
          on:mouseenter={() => changeCountries(country.i)}
          on:mouseleave={() => changeCountries([])} />
        {/each}
      </g>
      <g>
        {#each activeElements as country}
        <path
          d={country.d}
          fill={country.color}
          class="active" />
        {/each}
      </g>
    </g>
  </svg>
</div>

